import {
  IAccountProfile,
  IAccountProfilePreferences,
  IAccountProfileSocialNetwork,
  IAccountSecurity
} from '@core/models/account-profile.model';
import { IUserAboutMe, IUserAboutMePayload } from '@core/models';

interface IProfileInterestResponse {
  id: string;
  name: string;
  description: string | null;
}

interface IProfileEmailResponse {
  id: string;
  email: string;
  isPrimary: boolean;
  isLicenced: boolean;
}

export interface IProfileResponse {
  id: string;
  isLicensed: boolean;
  region: string;
  accountImages: { profilePictureUrl: string | null; bannerUrl: string | null };
  preferences: {
    title: string | null;
    pronouns: string | null;
    isCustomPronouns: boolean;
    preferredUsername: string;
    firstName: string | null;
    lastName: string | null;
    emailPrimary: string;
    jobTitle: string | null;
    department: string | null;
    currentLocation: string | null;
    mobileNumber: string;
    contactMethod: string | null;
    isTitleVisible: boolean;
    isPronounsVisible: boolean;
    isOrganizationVisible: boolean;
    organization: { id: string; name: string };
    accountEmails: IProfileEmailResponse[];
  };
  socialNetwork: {
    linkedinUrl: string | null;
    isLinkedinUrlVisible: boolean;
    twitterUrl: string | null;
    isTwitterUrlVisible: boolean;
    instagramUrl: string | null;
    isInstagramUrlVisible: boolean;
    orcidUrl: string | null;
    isOrcidUrlVisible: boolean;
    academiaUrl: string | null;
    isAcademiaUrlVisible: boolean;
    researchGateUrl: string | null;
    isResearchGateUrlVisible: boolean;
    mendeleyUrl: string | null;
    isMendeleyUrlVisible: boolean;
    websiteUrl: string | null;
    isWebsiteUrlVisible: boolean;
    elementsUsername: string | null;
    isElementsUsernameVisible: boolean;
  };
  accountSecurity: {
    emailPrimary: string;
    preferredUsername: string;
    mobileNumber: string;
    emailPrimaryVerified: boolean;
    mobileNumberVerified: boolean;
  };
  aboutMe: {
    interests: IProfileInterestResponse[];
    defaultInterests: IProfileInterestResponse[];
    sdgs: number[];
    biography: string | null;
    biographyText: string | null;
  };
}

export function mapProfileResponse(response: IProfileResponse): IAccountProfile {
  return {
    id: response.id,
    isLicensed: response.isLicensed,
    region: response.region,
    accountImages: {
      profilePictureUrl: response.accountImages.profilePictureUrl ?? undefined,
      bannerUrl: response.accountImages.bannerUrl ?? undefined
    },
    preferences: mapPreferences(response.preferences),
    socialNetwork: mapSocialNetwork(response.socialNetwork),
    accountSecurity: mapSecurity(response.accountSecurity),
    aboutMe: mapAboutMe(response.aboutMe)
  };
}

function mapPreferences(preferences: IProfileResponse['preferences']): IAccountProfilePreferences {
  return {
    title: preferences.title ?? '',
    pronouns: preferences.pronouns ?? '',
    isCustomPronouns: preferences.isCustomPronouns,
    preferredUsername: preferences.preferredUsername,
    firstName: preferences.firstName ?? '',
    lastName: preferences.lastName ?? '',
    emailPrimary: preferences.emailPrimary,
    jobTitle: preferences.jobTitle ?? '',
    department: preferences.department ?? '',
    currentLocation: preferences.currentLocation ?? '',
    mobileNumber: preferences.mobileNumber,
    contactMethod: preferences.contactMethod ?? '',
    isTitleVisible: preferences.isTitleVisible,
    isPronounsVisible: preferences.isPronounsVisible,
    isOrganizationVisible: preferences.isOrganizationVisible,
    organization: { id: preferences.organization.id, name: preferences.organization.name },
    accountEmails: preferences.accountEmails.map(email => ({
      id: email.id,
      email: email.email,
      isPrimary: email.isPrimary,
      isLicenced: email.isLicenced
    }))
  };
}

// The API names social links after the write endpoints (linkedinUrl, websiteUrl); the SPA model uses
// linkedIn/personalWebsite, so this is the read-side bridge between the two.
function mapSocialNetwork(social: IProfileResponse['socialNetwork']): IAccountProfileSocialNetwork {
  return {
    linkedIn: social.linkedinUrl ?? '',
    isLinkedInVisible: social.isLinkedinUrlVisible,
    twitter: social.twitterUrl ?? '',
    isTwitterVisible: social.isTwitterUrlVisible,
    instagram: social.instagramUrl ?? '',
    isInstagramVisible: social.isInstagramUrlVisible,
    orcid: social.orcidUrl ?? '',
    isOrcidVisible: social.isOrcidUrlVisible,
    academia: social.academiaUrl ?? '',
    isAcademiaVisible: social.isAcademiaUrlVisible,
    researchGate: social.researchGateUrl ?? '',
    isResearchGateVisible: social.isResearchGateUrlVisible,
    mendeley: social.mendeleyUrl ?? '',
    isMendeleyVisible: social.isMendeleyUrlVisible,
    personalWebsite: social.websiteUrl ?? '',
    isPersonalWebsiteVisible: social.isWebsiteUrlVisible,
    elementsUsername: social.elementsUsername ?? '',
    isElementsUsernameVisible: social.isElementsUsernameVisible
  };
}

function mapSecurity(security: IProfileResponse['accountSecurity']): IAccountSecurity {
  return {
    emailPrimary: security.emailPrimary,
    preferredUsername: security.preferredUsername,
    mobileNumber: security.mobileNumber,
    emailPrimaryVerified: security.emailPrimaryVerified,
    mobileNumberVerified: security.mobileNumberVerified
  };
}

function mapAboutMe(aboutMe: IProfileResponse['aboutMe']): IUserAboutMe {
  return {
    interests: aboutMe.interests.map(mapInterest),
    defaultInterests: aboutMe.defaultInterests.map(mapInterest),
    sdgs: aboutMe.sdgs,
    biography: aboutMe.biography ?? '',
    biographyText: aboutMe.biographyText ?? ''
  };
}

function mapInterest(interest: IProfileInterestResponse) {
  return { id: interest.id, name: interest.name, description: interest.description ?? undefined };
}

export interface ISocialNetworksRequest {
  linkedinUrl: string | null;
  isLinkedinUrlVisible: boolean;
  twitterUrl: string | null;
  isTwitterUrlVisible: boolean;
  instagramUrl: string | null;
  isInstagramUrlVisible: boolean;
  orcidUrl: string | null;
  isOrcidUrlVisible: boolean;
  academiaUrl: string | null;
  isAcademiaUrlVisible: boolean;
  researchGateUrl: string | null;
  isResearchGateUrlVisible: boolean;
  mendeleyUrl: string | null;
  isMendeleyUrlVisible: boolean;
  websiteUrl: string | null;
  isWebsiteUrlVisible: boolean;
  elementsUsername: string | null;
  isElementsUsernameVisible: boolean;
}

export function toSocialNetworksRequest(
  social: IAccountProfileSocialNetwork
): ISocialNetworksRequest {
  return {
    linkedinUrl: emptyToNull(social.linkedIn),
    isLinkedinUrlVisible: social.isLinkedInVisible,
    twitterUrl: emptyToNull(social.twitter),
    isTwitterUrlVisible: social.isTwitterVisible,
    instagramUrl: emptyToNull(social.instagram),
    isInstagramUrlVisible: social.isInstagramVisible,
    orcidUrl: emptyToNull(social.orcid),
    isOrcidUrlVisible: social.isOrcidVisible,
    academiaUrl: emptyToNull(social.academia),
    isAcademiaUrlVisible: social.isAcademiaVisible,
    researchGateUrl: emptyToNull(social.researchGate),
    isResearchGateUrlVisible: social.isResearchGateVisible,
    mendeleyUrl: emptyToNull(social.mendeley),
    isMendeleyUrlVisible: social.isMendeleyVisible,
    websiteUrl: emptyToNull(social.personalWebsite),
    isWebsiteUrlVisible: social.isPersonalWebsiteVisible,
    elementsUsername: emptyToNull(social.elementsUsername),
    isElementsUsernameVisible: social.isElementsUsernameVisible
  };
}

function emptyToNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

interface IAboutMeInterestRequest {
  id: string | null;
  name: string;
  description: string | null;
}

export interface IAboutMeRequest {
  biography: string | null;
  biographyPlainText: string | null;
  interests: IAboutMeInterestRequest[];
  sdgs: number[];
}

export function toAboutMeRequest(aboutMe: IUserAboutMePayload): IAboutMeRequest {
  return {
    biography: aboutMe.biography || null,
    biographyPlainText: aboutMe.biographyText || null,
    interests: aboutMe.interests.map(interest => ({
      id: interest.id || null,
      name: interest.name,
      description: interest.description || null
    })),
    sdgs: aboutMe.sdgs
  };
}

export interface IPreferencesRequest {
  firstName: string;
  lastName: string;
  jobTitle: string | null;
  department: string | null;
  currentLocation: string | null;
  title: string | null;
  isTitleVisible: boolean;
  pronouns: string | null;
  isCustomPronouns: boolean;
  isPronounsVisible: boolean;
  contactMethod: string | null;
  isOrganizationVisible: boolean;
  organizationId: string | null;
}

export function toPreferencesRequest(preferences: IAccountProfilePreferences): IPreferencesRequest {
  return {
    firstName: preferences.firstName,
    lastName: preferences.lastName,
    jobTitle: emptyToNull(preferences.jobTitle),
    department: emptyToNull(preferences.department),
    currentLocation: emptyToNull(preferences.currentLocation),
    title: emptyToNull(preferences.title),
    isTitleVisible: preferences.isTitleVisible,
    pronouns: emptyToNull(preferences.pronouns),
    isCustomPronouns: preferences.isCustomPronouns,
    isPronounsVisible: preferences.isPronounsVisible,
    contactMethod: emptyToNull(preferences.contactMethod),
    isOrganizationVisible: preferences.isOrganizationVisible,
    organizationId: emptyToNull(preferences.organization?.id)
  };
}
